import { useOnChange } from "@prismicio/editor-support/React"
import type { Editor } from "@tiptap/core"
import DocumentExtension from "@tiptap/extension-document"
import HardBreakExtension from "@tiptap/extension-hard-break"
import HistoryExtension from "@tiptap/extension-history"
import ItalicExtension from "@tiptap/extension-italic"
import ParagraphExtension from "@tiptap/extension-paragraph"
import PlaceholderExtension from "@tiptap/extension-placeholder"
import TextExtension from "@tiptap/extension-text"
import { EditorContent } from "@tiptap/react"
import clsx from "clsx"

import styles from "./ContentEditable.module.css"
import { useEditor } from "./useEditor"

interface ContentEditableProps {
  /** The raw text or HTML content */
  content: string
  /**
   * Bump the contentVersion when you need to reset/resync the content from the outside,
   * for instance when the content is a radically different thing.
   * This is pretty much like <ContentEditable key={contentVersion} /> except the Tiptap Editor remains mounted and doesn't bug out/flicker.
   * tiptap doesn't officially support being controlled and it results in cursor/concurrency issues.
   * If you do contentVersion={content}, this is essentially like controlling the component
   * from the parent but it only works well for a readOnly ContentEditable.
   */
  contentVersion?: string | number
  readOnly?: boolean
  placeholder?: string
  multiline?: boolean
  padding?: boolean
  /** Overrides the default styles of HTML tags */
  decorations?: Decorations
  onChange?: (editor: Editor) => void
  onBlur?: (editor: Editor) => void
}

export function ContentEditable(props: ContentEditableProps) {
  const {
    content,
    contentVersion,
    readOnly = false,
    multiline = false,
    placeholder,
    padding = multiline,
    decorations = {},
    onChange,
    onBlur
  } = props

  const extensions = [
    DocumentExtension.extend({ content: multiline ? "block+" : "block" }),
    PlaceholderExtension.configure({ placeholder }),
    ParagraphExtension,
    TextExtension,
    ItalicExtension,
    HistoryExtension,
    ...(multiline ? [HardBreakExtension] : [])
  ]

  const editor = useEditor(
    {
      content,
      editable: !readOnly,
      extensions,
      editorProps: {
        attributes: {
          class: clsx(
            styles.contentEditable,
            styles[`padding-${padding ? "normal" : "zero"}`],
            styles[`em-${decorations.em ?? "normal"}`],
            styles[`p-${decorations.p ?? "normal"}`],
            !multiline && styles.noWrap
          )
        }
      },
      onUpdate({ editor }) {
        onChange?.(editor)
      },
      onBlur({ editor }) {
        onBlur?.(editor)
      }
    },
    [readOnly]
  )

  const style = {
    flexGrow: 1,
    width: multiline ? undefined : "100%"
  }

  useOnChange(contentVersion, () => editor?.commands.setContent(content))

  return <EditorContent editor={editor} style={style} />
}

interface Decorations {
  em?: "normal" | "highlight"
  p?: "normal" | "emphasized"
}
